import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ReactHtmlParser from 'react-html-parser';
import Truncate from 'react-truncate';
import Dialog from '@mui/material/Dialog';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';

import RequiredQuestionLabel from 'components/job_or_assessment_settings/Chip';
import { nl2brCustom } from 'helper/commonFunctions';

const ImmersiveDialog = ({
    question,
    userName,
    questionNumber,
    onClose,
    open,
    previewLink
}) => {
    const [titleOpened, setTitleOpened] = useState(false);
    const [truncated, setTruncated] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const iframeRef = useRef(null);

    const toggleTitleOpen = useCallback((e) => {
        if (e?.preventDefault) e.preventDefault();
        setTitleOpened(prev => !prev);
    }, []);

    const handleTruncate = useCallback((isTruncated) => {
        setTruncated((prev) => {
            if (isTruncated && !prev) {
                setTitleOpened(!isTruncated);
            } else if (!isTruncated) {
                setTitleOpened(true);
            }
            return isTruncated;
        });
    }, []);

    const iframeSrc = useMemo(() => {
        if (previewLink) return previewLink;
        if (question?.googleDriveFile?.previewLink) {
            return `${question.googleDriveFile.previewLink}&rm=embedded`;
        }
        return null;
    }, [previewLink, question]);

    useEffect(() => {
        setIframeError(false);
    }, [open, iframeSrc]);

    useEffect(() => {
        const onMessage = (event) => {
            const { type, status } = event.data || {};
            if (type === 'iframe-error' && status === 400) {
                const fromOurIframe = iframeRef.current && event.source === iframeRef.current.contentWindow;
                if (fromOurIframe) setIframeError(true);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    if (!question) return null;

    const { required, description } = question;

    return (
        <Dialog fullScreen open={open}>
            <Box
                className="c-reg-step-dialog"
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-start',
                    px: 2,
                    pt: 2,
                    pb: 1
                }}
            >
                <Collapse
                    in={titleOpened || !truncated}
                    collapsedSize={truncated ? '76px' : '0px'}
                    sx={{ maxWidth: 700 }}
                >
                    <Box className="u-mrg--tx2 u-mrg--bx2 u-txt--18 u-dsp--f u-ai--center" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <div>
                            {questionNumber !== undefined && <>Question {questionNumber + 1}</>}
                            {userName ? ` - ${userName}` : ''}
                        </div>
                        {required && <RequiredQuestionLabel uppercase className="u-mrg--lx2" />}
                    </Box>

                    {!titleOpened && (
                        <div>
                            <Truncate
                                lines={2}
                                ellipsis={
                                    <>
                                        ...{' '}
                                        <button onClick={toggleTitleOpen} className="c-button-link t-txt--brand">
                                        (show more)
                                        </button>
                                    </>
                                }
                                onTruncate={handleTruncate}
                            >
                                {Boolean(description) && ReactHtmlParser(nl2brCustom(description))}
                            </Truncate>
                        </div>
                    )}

                    {titleOpened && Boolean(description) && ReactHtmlParser(nl2brCustom(description))}

                    {titleOpened && truncated && (
                        <button onClick={toggleTitleOpen} className="c-button-link t-txt--brand">
              &nbsp;(show less)
                        </button>
                    )}

                    {!titleOpened && !truncated && Boolean(description) && ReactHtmlParser(nl2brCustom(description))}
                </Collapse>

                <IconButton
                    onClick={onClose}
                    size="large"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                    aria-label="Close"
                >
                    <CloseIcon fontSize="large" />
                </IconButton>
            </Box>
            {iframeError ? (
                <Box
                    sx={{
                        height: 'calc(100vh - 90px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 2,
                        textAlign: 'center'
                    }}
                >
                    <span>Cannot open the file</span>
                </Box>
            ) : (
                <iframe
                    ref={iframeRef}
                    title="Grade card 2"
                    frameBorder="0"
                    src={iframeSrc || undefined}
                    width="100%"
                    height="100%"
                    style={{ position: 'relative', left: 0, height: 'calc(100vh - 90px)' }}
                />
            )}
        </Dialog>
    );
};

export default ImmersiveDialog;
